import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { inr } from "@/lib/format";

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);

  const refresh = () => api.get("/admin/plans").then(({ data }) => setPlans(data));
  useEffect(() => { refresh(); }, []);

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="admin-plans-title">Subscription Plans</h1>
          <p className="text-slate-500 text-sm mt-1">Pre-defined and custom plans tenants can subscribe to.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="admin-create-plan-btn"><Plus className="h-4 w-4 mr-2" /> New Plan</Button>
          </DialogTrigger>
          <DialogContent><DialogHeader><DialogTitle>Create Plan</DialogTitle></DialogHeader>
            <PlanForm onSaved={() => { setOpen(false); refresh(); }} />
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {plans.map((p) => (
          <Card key={p.id} className="p-6 border-slate-200 relative" data-testid={`admin-plan-${p.name.toLowerCase()}`}>
            <div className="flex justify-between">
              <h3 className="font-display text-xl font-semibold">{p.name}</h3>
              <Button size="icon" variant="ghost" data-testid={`admin-plan-${p.name.toLowerCase()}-delete`} onClick={async () => {
                if (!confirm("Delete plan?")) return;
                await api.delete(`/admin/plans/${p.id}`);
                refresh();
              }}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
            </div>
            <div className="my-3"><span className="text-3xl font-display font-bold">{inr(p.price)}</span><span className="text-sm text-slate-500"> / {p.billing_cycle}</span></div>
            {p.trial_days > 0 && <div className="text-xs text-emerald-700 mb-3">+ {p.trial_days}-day free trial</div>}
            <ul className="text-sm space-y-1 text-slate-700">
              {(p.features || []).map((f, i) => <li key={i}>• {f}</li>)}
            </ul>
            <div className="border-t border-slate-100 mt-4 pt-3 grid grid-cols-2 gap-1 text-xs text-slate-600">
              <div>Products: {p.limits?.max_products}</div>
              <div>Categories: {p.limits?.max_categories}</div>
              <div>Staff: {p.limits?.max_staff}</div>
              <div>Customers: {p.limits?.max_customers}</div>
              <div>Orders/mo: {p.limits?.max_orders_per_month}</div>
              <div>Storage: {p.limits?.storage_mb}MB</div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function PlanForm({ onSaved }) {
  const [form, setForm] = useState({
    name: "",
    price: 999,
    billing_cycle: "monthly",
    trial_days: 7,
    limits: { max_products: 100, max_categories: 20, max_staff: 5, max_customers: 1000, max_orders_per_month: 500, storage_mb: 1000 },
    features: ["Custom Branding"],
    is_custom_for_tenant_id: null,
  });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/plans", form);
      toast.success("Plan created");
      onSaved();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <form className="space-y-3" onSubmit={submit}>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Name</Label><Input data-testid="plan-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
        <div><Label>Price (₹)</Label><Input data-testid="plan-price-input" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) })} /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Cycle</Label>
          <Select value={form.billing_cycle} onValueChange={(v) => setForm({ ...form, billing_cycle: v })}>
            <SelectTrigger data-testid="plan-cycle-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="quarterly">Quarterly</SelectItem>
              <SelectItem value="yearly">Yearly</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>Trial days</Label><Input data-testid="plan-trial-input" type="number" value={form.trial_days} onChange={(e) => setForm({ ...form, trial_days: parseInt(e.target.value || "0") })} /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Max Products</Label><Input type="number" value={form.limits.max_products} onChange={(e) => setForm({ ...form, limits: { ...form.limits, max_products: parseInt(e.target.value || "0") } })} /></div>
        <div><Label>Max Staff</Label><Input type="number" value={form.limits.max_staff} onChange={(e) => setForm({ ...form, limits: { ...form.limits, max_staff: parseInt(e.target.value || "0") } })} /></div>
      </div>
      <div><Label>Features (comma)</Label><Input value={form.features.join(",")} onChange={(e) => setForm({ ...form, features: e.target.value.split(",").map(s => s.trim()) })} /></div>
      <Button type="submit" className="w-full" data-testid="plan-save-btn">Save Plan</Button>
    </form>
  );
}
